import { useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ActionIcon, TextInput } from '@any-ui-react/core';
import { useLens } from '@hookform/lenses';

import {
  SortableElement,
  SortableRoot,
  DragHandler,
  AddMessageElement,
} from '@anyx/feature-crm/shared';
import { TrashIcon } from '@anyx/shared-icons';

import { useLineMessageStore } from '../../store';
import { LineMessageFormFieldsProps, ButtonTypeBlockFieldErrors } from '../../types';
import { DEFAULT_BUTTON_TYPE_BLOCK_CARD_ACTION } from '../../utils';

import { ButtonTypeActionTypeController } from './ButtonTypeActionTypeController';

const MAX_BUTTON_TYPE_LABEL_LENGTH = 12;
export function ButtonTypeSortableActionButton({
  id,
  blockIndex,
  cardIndex,
  buttonIndex,
  onRemove,
}: {
  id: string;
  blockIndex: number;
  cardIndex: number;
  buttonIndex: number;
  onRemove?: () => void;
}) {
  const { t } = useTranslation('crm');
  const isFormDisabled = useLineMessageStore((store) => store.isFormDisabled);

  const { register, control, formState } = useFormContext<LineMessageFormFieldsProps>();

  const trackingEnabled = useWatch({
    control,
    name: 'trackingEnabled',
  });

  const actionLabel = useWatch({
    control,
    name: `messages.${blockIndex}.cards.${cardIndex}.actions.${buttonIndex}.label` as const,
  });

  const watchPostbackAction = useWatch({
    control,
    name: `messages.${blockIndex}.cards.${cardIndex}.actions.${buttonIndex}.postbackAction` as const,
  });

  const lens = useLens({
    control,
  });

  const error = useMemo(() => {
    const errorMsg = formState.errors?.messages?.[blockIndex] as ButtonTypeBlockFieldErrors;

    if (errorMsg && 'cards' in errorMsg) {
      const cardMsg = errorMsg.cards?.[cardIndex];

      if (cardMsg && 'actions' in cardMsg) {
        return cardMsg.actions?.[buttonIndex];
      }
    }

    return undefined;
  }, [blockIndex, formState, cardIndex, buttonIndex]);

  return (
    <li className="grid grid-cols-[1fr_auto] gap-x-2">
      <SortableElement id={id} className="bg-white! py-2">
        {({ dragListeners }) => (
          <div className="flex items-center gap-x-2 py-2 pr-6 pl-2">
            {!isFormDisabled && <DragHandler {...dragListeners} />}
            <div className="flex grow flex-col gap-y-1">
              <TextInput
                {...register(
                  `messages.${blockIndex}.cards.${cardIndex}.actions.${buttonIndex}.label` as const
                )}
                inputWrapperOrder={['label', 'input', 'error', 'description']}
                classNames={{
                  description: 'text-right',
                }}
                required
                readOnly={isFormDisabled}
                label={t('crm.lineMessageForm.fields.actionLabel.label')}
                placeholder={t('crm.lineMessageForm.fields.actionLabel.placeholder')}
                description={
                  <>
                    <span className="font-semibold">{actionLabel?.length || 0}</span>/
                    {MAX_BUTTON_TYPE_LABEL_LENGTH}
                  </>
                }
                error={error?.label?.message}
                maxLength={MAX_BUTTON_TYPE_LABEL_LENGTH}
              />

              <ButtonTypeActionTypeController
                lens={lens.reflect((_, l) => ({
                  postbackAction: l.focus(
                    `messages.${blockIndex}.cards.${cardIndex}.actions.${buttonIndex}.postbackAction` as const
                  ),
                  manual: l.focus(
                    `messages.${blockIndex}.cards.${cardIndex}.actions.${buttonIndex}.url` as const
                  ),
                  variable: l.focus(
                    `messages.${blockIndex}.cards.${cardIndex}.actions.${buttonIndex}.urlVariable` as const
                  ),
                }))}
                postbackAction={watchPostbackAction}
                trackingEnabled={trackingEnabled}
              />
            </div>
          </div>
        )}
      </SortableElement>
      {!isFormDisabled && (
        <ActionIcon variant="subtle" size="sm" onClick={onRemove}>
          <TrashIcon className="h-4 w-4" />
        </ActionIcon>
      )}
    </li>
  );
}

export function ButtonTypeActionButtons({
  blockIndex,
  cardIndex,
}: {
  blockIndex: number;
  cardIndex: number;
}) {
  const { t } = useTranslation('crm');
  const isFormDisabled = useLineMessageStore((store) => store.isFormDisabled);

  const { control } = useFormContext<LineMessageFormFieldsProps>();

  const { fields, append, move, remove } = useFieldArray({
    control,
    name: `messages.${blockIndex}.cards.${cardIndex}.actions` as const,
  });

  const itemsId = useMemo(() => {
    return fields.map(({ id }) => id);
  }, [fields]);

  return (
    <div className="flex flex-col gap-y-2 py-4">
      <SortableRoot itemsId={itemsId} className="space-y-2" onMove={move}>
        {fields.map(({ id }, buttonIndex) => (
          <ButtonTypeSortableActionButton
            // Composite key: remount the row when its index shifts (e.g. after deleting a
            // non-last button) so RHF value bindings re-bind to the new index. dnd-kit still
            // uses the pure `id` below, so drag/sort is unaffected.
            key={`${id}_${buttonIndex}`}
            id={id}
            blockIndex={blockIndex}
            cardIndex={cardIndex}
            buttonIndex={buttonIndex}
            onRemove={() => remove(buttonIndex)}
          />
        ))}
      </SortableRoot>
      {!isFormDisabled && (
        <AddMessageElement
          currentLength={fields.length}
          maxLength={10}
          onAppend={() => {
            append(DEFAULT_BUTTON_TYPE_BLOCK_CARD_ACTION);
          }}
          label={t('crm.lineMessageForm.actions.addActionBtn')}
        />
      )}
    </div>
  );
}
